// WHO HOLDS THE LAST SEAT ON A BRAND'S LADDERS — the vocabulary, the default
// and what a stored row looks like.
//
// Pure, store-free and React-free, for exactly the reason lib/commitment.ts is:
// BOTH SIDES NEED IT AND ONLY ONE OF THEM MAY TOUCH SQLITE. The Schedule page
// writes it from a browser, the script store reads it on every stage move, and
// the server's audit map names it — a definition living in a server module would
// drag better-sqlite3 into every component that wanted to know what the default
// is.
//
// ⚠️ `brand_approval` IS A WIRE VALUE, and so is its id: the BRAND SLUG, the
// same key `brand_schedule` is written under. There is no migration runner, and
// renaming either is a data migration nobody has a tool for. A slug is permanent
// and a display name is not, which is the whole reason the key is what it is.

import type { FinalApprover } from "./stage-model";

export type { FinalApprover };

/** The `store` kind. ⚠️ A wire value — see the header. */
export const APPROVAL_KIND = "brand_approval";

/**
 * THE SEAT A BRAND HAS WHEN NOBODY HAS SAID ANYTHING — and it is today's flow.
 *
 * ⚠️ A DEFAULT HERE IS NOT THE THING THE NO-INVENTED-FIGURES RULE FORBIDS. That
 * rule is about FIGURES: a number nobody set may never render as if somebody
 * had, because the page's whole claim is that nothing on it was invented. This
 * is not a figure, it is a flow, and a flow has to work for a brand nobody has
 * configured. An absent row means the two-seat ladder every brand has run on
 * since the app existed — which is the `DEFAULT_SCHEDULE` precedent exactly:
 * absent config falls back to a named constant, never to a guess.
 */
export const DEFAULT_FINAL_APPROVER: FinalApprover = "brand";

/**
 * One stored row. `setBy` is the provenance half; the store's own `updated_at`
 * is the other, so nothing here has to carry a clock — the same split
 * `Commitment` makes, for the same reason.
 */
export interface BrandApproval {
  finalApprover: FinalApprover;
  /** The acting persona who chose it. A negotiated arrangement carries a name. */
  setBy: string;
}

/** Is this one of the two seats? The type guard the reader and the page share. */
export function isFinalApprover(value: unknown): value is FinalApprover {
  return value === "brand" || value === "am";
}

/**
 * One stored row, or null for anything this app did not write.
 *
 * ⚠️ IT VALIDATES ON THE WAY OUT, not only on the way in. These rows arrive from
 * `/api/store` as `unknown`, and a row written by an older client — or by hand —
 * carrying `"Brand"`, `true` or a typo would otherwise become a seat. A row we
 * cannot read is treated as ABSENT, which falls back to `"brand"`: the two-seat
 * ladder is the safe misreading, because it asks for an approval that may not be
 * needed rather than granting one nobody gave.
 */
export function readApproval(data: unknown): BrandApproval | null {
  if (!data || typeof data !== "object") return null;
  const seat = (data as { finalApprover?: unknown }).finalApprover;
  if (!isFinalApprover(seat)) return null;
  const setBy = (data as { setBy?: unknown }).setBy;
  return { finalApprover: seat, setBy: typeof setBy === "string" ? setBy : "" };
}

// ─── What the control says ──────────────────────────────────────────────────
//
// HERE RATHER THAN IN THE PAGE, because the Schedule board renders the choice
// and the milestone list underneath it renames two of its own rows from the same
// value. Two spellings of one arrangement, a screen apart, is the drift this
// file exists to prevent.

/** The option as the Schedule page offers it, and the line under it. */
export const APPROVER_CHOICE: ReadonlyArray<{
  key: FinalApprover;
  label: string;
  note: string;
}> = [
  {
    key: "brand",
    label: "Brand approves after the AM",
    note: "The usual arrangement: an AM clears the work, then the brand rules on it.",
  },
  {
    key: "am",
    label: "The AM's approval is final",
    note: "For brands who would rather not review. Work is approved the moment their AM clears it, and the brand sees it as approved from that moment.",
  },
];
